/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([], () => {
	const AUTO_MULTI_WIDGET_SELECTION_SUPPORTED_BY_DEFAULT = [
		// Multi widget selections alreday implemented by the feature js/features/content/alignProperties/AlignPropertiesProvider
		// Please see AlignPropertiesProvider implementation to see how they support setting same property value to multiple
		// widget selections
		'general.layout.alignWidget'
	];

	const FUNCTIONS_TO_EXTEND = [
		'onChange',
		'onChangePalette'
	];

	const INPUT_LABEL = 'InputLabel';

	/**
	 * V1 Properties Helper class ONLY
	 * V1 Properties do not use this class
	 * This class extends LayoutPropertyList onChange/onChangePalette to support apply the same property value to all current
	 * selected widget such that the changes are undo/redo-able in one unit transaction token
	 * This class however is not responsible for ensuring whether a property is applicable to a selected a widget.
	 * The process that filtered out common properties is already supported by the dashboard.
	 */
	return class ExtendLayoutPropertiesV1Helper {
		constructor({ propertyLayoutSpec = [], contents = [], dashboard, transaction }) {
			if (propertyLayoutSpec.length !== contents.length) {
				throw new Error('propertyLayoutSpec and contents array size do not match');
			}
			this.propertyLayoutSpec = propertyLayoutSpec;
			this.contents = contents;
			this.dashboard = dashboard;
			this.transaction = transaction;
			this.callbackMap = {};
			return {
				extendLayoutProperties: this._extendLayoutProperties.bind(this)
			};
		}

		/**
		 * Call to extend the onChange/onChangePalette to set property to the set of selected widget(s) that are undo/redo-able
		 * in one transaction token
		 */
		_extendLayoutProperties () {
			this.propertyLayoutSpec.forEach((spec, index) => {
				const sectionIds = [];
				this._extendPropertyLayoutSpec(this.contents[index], spec, sectionIds);
			});
		}

		/**
		 * Recursive walk each items array to extend the onChange/onChangePalette callbacks to support
		 * applying the same property value to all selected layouts on the canvas
		 *
		 * @param {object} content - the content Api object
		 * @param {array} specItems - optional array of item spec
		 * @param {array} sectionIds - array of unique section ids
		 */
		_extendPropertyLayoutSpec (content, specItems, sectionIds) {
			if (Array.isArray(specItems)) {
				specItems.forEach(item => {
					const uniqueIds = JSON.parse(JSON.stringify(sectionIds));
					uniqueIds.push(item.id);
					let uniqueId = uniqueIds.join('.');
					FUNCTIONS_TO_EXTEND.forEach(functionName => {
						if (item[functionName]) {
							// Some id like left, right are not unique enough so therefore
							// use unique ids join by its ancestors
							if (!AUTO_MULTI_WIDGET_SELECTION_SUPPORTED_BY_DEFAULT.includes(uniqueId)) {
								const uniqueCallbackId = `${uniqueId}__${functionName}`;
								const callback = item[functionName];
								this._addCallbackToMap(uniqueCallbackId, content, item, callback);
								// Redefine the callback that supports multiple widget selections setting the same property value
								// accepting arbitrary number of arguments.
								// core-client invoke call back passing in arbitrary number of arguments depending on which
								// core-client UI
								item[functionName] = (...args) => {
									this._executeCallbacks(uniqueCallbackId, ...args);
								};
							}
						}
					});
					this._extendPropertyLayoutSpec(content, item.items, uniqueIds);
				});
			}
		}

		/**
		 * Add the callback to map key by unique id
		 *
		 * @param {string} uniqueCallbackId - the unique callback string id
		 * @para {function} callback - the callback function
		 */
		_addCallbackToMap (uniqueCallbackId, content, item, callback) {
			if (!this.callbackMap[uniqueCallbackId]) {
				this.callbackMap[uniqueCallbackId] = [];
			}
			this.callbackMap[uniqueCallbackId].push({
				content: content,
				callback: callback,
				property: item
			});
		}

		/**
		 * Execute all callbacks key by uniqueId
		 *
		 * @param {string} uniqueCallbackId - the unique callback string id
		 * @para {array} args - arbitrary nunber of arguments
		 */
		_executeCallbacks (uniqueCallbackId, ...args) {
			const callbacks = this.callbackMap[uniqueCallbackId] || [];
			if (callbacks.length) {
				let forceRefreshPropertiesPanel = false;
				const params = [...args];
				// For V1 Properties we only need the first 2 arguments.
				if (params.length > 2) {
					params.length = 2;
				}

				// Same transaction so that undo/redo work as a unit of changes
				const transactionToken = this.transaction.startTransaction();
				callbacks.forEach(context => {
					const { content, property, callback } = context;
					const originalValue = content.getPropertyValue(property.id);
					callback(...[...params, transactionToken]);
					const newValue = content.getPropertyValue(property.id);
					if (!forceRefreshPropertiesPanel) {
						// Only force refresh when the values are the same and defined (undefined value do not need refresh)
						// As soon as encounter one that is not the same then
						// force refresh is false
						forceRefreshPropertiesPanel = property.type === INPUT_LABEL && (originalValue != null || newValue != null) && (originalValue === newValue);
					}
				});
				this.transaction.endTransaction(transactionToken);
				// Only refresh V1 Properties Panel when the values set in the UI is the same value store in dashboard spec
				if (forceRefreshPropertiesPanel) {
					this.dashboard.triggerDashboardEvent('properties:refreshPane');
				}
			}
		}
	};
});
